<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

import { PaperClipIcon } from '@heroicons/vue/24/outline';
import FileInput from '@/components/files/FileInput.vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CheckIcon } from '@heroicons/vue/24/outline'
import { ClaimOverview } from '@/pages/plugin/AgentFeedback.vue';
import adminAPI from '@/services/api';
import type { LoadedFile } from '../files/types/index';
import { useNotificationStore, useClaimStore } from '@/store';
import Alert from '../ui/alerts/Alert.vue';
import { storeToRefs } from 'pinia';
import BoxLinks from './BoxLinks.vue';
import BaseButton from '../ui/buttons/BaseButton.vue';
import { DownloadCloudIcon, Send } from 'lucide-vue-next';
import downloadFileFromResponse from '@/services/api/download-file-from-response';
import DividerCenterMedium from '../ui/dividers/DividerCenterMedium.vue';
import BaseInput from '../ui/inputs/BaseInput.vue';


const props = defineProps<{ claimOverview: ClaimOverview }>();
const { claim } = storeToRefs(useClaimStore());

// Kind of legacy - This part might not be really in use anymore
const isExpectedState = claim.value.claimState.type === 'CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES';

const files = ref<LoadedFile[]>([]);

async function submitForm(files: LoadedFile[]) {
  //  addClaimDocument
  const waitNotification = useNotificationStore().emitNotification({
    type: "wait",
    title: "Document en cours d'envoi"
  })
  for (const { file, content, documentType } of files) {
    const res = await adminAPI.addClaimDocument(props.claimOverview.claimId, {
      name: file.name,
      document: content,
      mimeType: file.type,
      claimDocumentType: documentType || "OTHER"
    });
  }
  useNotificationStore().emitNotification({
    title: "Document ajouté avec succès",
    type: "success",
  });
  waitNotification.duration = 0;
  open.value = true;
}
const open = ref(false);

async function setRequestDate() {
  try {
    await adminAPI.setAdditionalLossRequestDate(props.claimOverview.claimId);
    await useClaimStore().refreshClaim();
    useNotificationStore().emitNotification({ title: "Information enregistrée" });
  } catch (error) {
    console.error(error);
    useNotificationStore().emitNotification({
      type: "error",
      title: "Une erreur est survenue, merci de nous contacter"
    });
  }
}

const loading = ref(false);
async function downloadReceivables() {
  loading.value = true;
  const loadingNotification = useNotificationStore().emitNotification({
      type: "wait",
      title: "Document en cours de génération (~30 sec à 1min)...",
    }, 90000);
  try {
    const claimId = useClaimStore().claim?.id;
    const documentType = "receivable-contract";
    const response = await adminAPI.instance.post(
      `/admin/claims/${claimId}/documents/${documentType}`,
      null,
      { responseType: "blob" }
    );
    downloadFileFromResponse(response);
    useNotificationStore().emitNotification({
      type: "success",
      title: "Cessions générées avec succès",
      
    });
  } catch (error) {

  } finally {
    loadingNotification.duration = 0;
    loading.value = false;
  }
}
</script>
<template>
  <div>
    <!-- After submit modal -->
    <TransitionRoot as="template" :show="open">
      <Dialog as="div" class="relative z-10" @close="open = true">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
                    <CheckIcon class="w-6 h-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Informations enregistrées</DialogTitle>
                    <div class="mt-2">
                      <!-- <p class="text-sm text-slate-500">Nous avons bien enregistré votre retour sur ce dossier d'indemnisation.</p> -->
                      <p class="mt-4 text-sm text-slate-500">Un email contenant les documents à faire signer aux passagers va vous être envoyé.</p>
                    </div>
                  </div>
                </div>
                <div class="mt-5 sm:mt-6">
                  <router-link :to="{ name: 'ClaimOverview', params: { id: claimOverview.claimId } }" class="inline-flex justify-center w-full px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    @click="open = true">
                    Aller au dashboard
                  </router-link>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    <form v-if="isExpectedState" class="px-4">
      <div class="space-y-12">
          <div class="py-6">
            <DividerCenterMedium text="Documents à faire signer" />
            <div class="my-12">
              <Alert type="info" title="Nouveau !" message="Vous pouvez désormais télécharger les documents à faire signer, directement en cliquant ci-dessous :" :with-border="false" />
              <div class="my-6">
                <BaseButton 
                  size="md"
                  type="submit"
                  @click="downloadReceivables"
                  text="Télécharger les documents"
                  :icon="DownloadCloudIcon"
                  :loading="loading"
                  class="mx-auto"
                />
              </div>
              <div class="my-6" v-permission="['UPCLAIM_ADMIN', 'CUSTOMER_ACCOUNT_MANAGER']">
                <h3 class="mt-10 text-base italic font-semibold leading-7">Ou bien, faire signer l'entreprise électroniquement :</h3>

                <div v-if="claimOverview.travelAgencyCompany.isDirectPassengerContactAllowed">
                  <div v-if="true">
                    <BaseInput placeholder="Adresse email du référent" />
                    <BaseInput placeholder="Nom complet du signataire" />
                    <div class="pt-4">
                      <BaseButton 
                        @click="downloadReceivables"
                        text="Envoyer la signature électronique"
                        :icon="Send"
                        :loading="loading"
                        class="mx-auto"
                      />
                    </div>
                  </div>
                  <div v-else class="inline-flex items-center mt-2 text-sm italic text-gray-500">
                    <span class="mr-2 text-green-700"><BadgeCheckIcon :size="16" /></span>
                    Signature envoyée il y a {{ formatDistanceToNow(new Date(), { locale: fr }) }}
                  </div>
                </div>
                <div v-else class="mt-12">
                  <Alert type="warning" title="Signature électronique indisponible" message="<b>Contact direct interdit :</b> Il faut faire signer l'entreprise ET le passager manuellement" :with-border="false" />
                </div>
              </div>
            </div>
            <div class="my-24">
              <BoxLinks :claim-id="claim.id" />
            </div>
          </div>
      </div>
    </form>
    <div v-else>
      <Alert
        type="success"
        title="Dossier confirmé"
        message="Vous pouvez désormais générer et télécharger les cessions à faire signer"
        :with-border="false"
      />

      <div class="mt-12 bg-slate-50">
        <div class="px-6 pt-8 bg-white border-2 rounded">
          <div>
            <BaseButton 
              size="lg"
              @click="downloadReceivables"
              text="Télécharger maintenant les documents"
              :icon="DownloadCloudIcon"
              :loading="loading"
            />
          </div>
        </div>
      </div>      
    </div>
  </div>
</template>
<style scoped>

iframe {
  border: none;
  overflow: hidden;
  scrolling: "no";
}

</style>